import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Edit2, Eye, Save, ChevronRight, 
  Plus, X, ChevronLeft, ChevronDown 
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { RichTextEditor } from '../components/RichTextEditor';
import { ContentSidebar } from '../components/ContentSidebar';
import { TableOfContents } from '../components/TableOfContents';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProgressIndicator } from '../components/ProgressIndicator';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { useToast } from '../components/ui/use-toast';

const initialContent = `
# Getting Started with Arrays

Arrays are fundamental data structures that store elements in contiguous memory locations. They provide fast access to elements using indices and are widely used in programming.

## Key Concepts

1. **Indexing**: Arrays use zero-based indexing
2. **Contiguous Memory**: Elements are stored in consecutive memory locations
3. **Fixed Size**: Arrays have a fixed size in most programming languages

\`\`\`javascript
// Example array operations
const arr = [1, 2, 3, 4, 5];
arr.push(6);     // Add to end
arr.pop();       // Remove from end
arr.shift();     // Remove from start
arr.unshift(0);  // Add to start
\`\`\`

## Time Complexity

Common array operations and their time complexities:

- Access: O(1)
- Search: O(n)
- Insertion: O(n)
- Deletion: O(n)

> Understanding array operations' time complexity is crucial for writing efficient code.

## Common Patterns

Arrays are often used in various algorithmic patterns:

1. Two Pointer Technique
2. Sliding Window
3. Binary Search
4. Prefix Sum

## Practice Problems

Start with these problems to master array concepts:

1. Two Sum
2. Maximum Subarray
3. Container With Most Water
`;

export function ContentPage() {
  const [content, setContent] = useState(initialContent);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentSection, setCurrentSection] = useState<any>(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { toast } = useToast();

  const handleSectionSelect = (section: any) => {
    setCurrentSection(section);
    if (section.slug) {
      navigate(`/topic/${section.slug}`);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: "Success",
        description: "Content saved successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save content",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      <ResizablePanelGroup direction="horizontal">
        <ResizablePanel 
          defaultSize={25} 
          minSize={15} 
          maxSize={40}
          collapsible
          collapsedSize={0}
          onCollapse={() => setIsSidebarCollapsed(true)}
          onExpand={() => setIsSidebarCollapsed(false)}
        >
          <ContentSidebar
            onSectionSelect={handleSectionSelect}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={75}>
          <div className="h-full flex flex-col overflow-hidden">
            <div className="p-6 bg-white border-b space-y-4">
              <div className="max-w-4xl mx-auto w-full">
                <Breadcrumbs 
                  items={[
                    { label: 'Content', href: '/content' },
                    ...(currentSection ? [{ label: currentSection.title }] : [])
                  ]} 
                />

                <div className="flex justify-between items-center mt-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {currentSection?.title || 'Introduction to Arrays'}
                    </h1>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {isAdmin && (
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-4 py-2 rounded-md flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        {isEditing ? (
                          <>
                            <Eye className="w-4 h-4" />
                            <span>Preview</span>
                          </>
                        ) : (
                          <>
                            <Edit2 className="w-4 h-4" />
                            <span>Edit</span>
                          </>
                        )}
                      </button>
                    )}
                    {isEditing && (
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-md flex items-center space-x-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        <span>{isSaving ? 'Saving...' : 'Save'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {currentSection && (
                  <ProgressIndicator sectionId={currentSection.id} />
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-white">
              <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                  <div className="lg:col-span-3">
                    <RichTextEditor 
                      content={content} 
                      onChange={setContent}
                      editable={isEditing}
                      onSave={handleSave}
                    />
                  </div>
                  <div className="lg:col-span-1">
                    <TableOfContents content={content} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}